/**
 * Shared surface tokens for `Tooltip` and `Popover` (Figma "Tooltip" set,
 * file `YZK9r3wDhvw1pY60KlVEIj`).
 *
 * BOTH components have a **Simple** and a **Rich** form — the only thing that
 * separates a Tooltip from a Popover is behaviour (hover + no actions vs
 * press/click + actions), NOT the list style. So the surface geometry and
 * typography live here and are shared by all four platform files.
 *
 *   - Simple (`List=Simple`, node 2205:13006): a compact single-region hint —
 *     16px radius, 16/10 padding.
 *   - Rich   (`List=Rich`,   node 2205:12922): a padded card with a Bold title
 *     + description (+ optional action on Popover) — 20px radius, 16px padding,
 *     4px vertical rhythm.
 *
 * A component is rendered Rich whenever it is given a `title`; otherwise Simple.
 *
 * Plain `.ts` (no React / RN / Radix imports) so it is safe to import from the
 * web `.tsx` and native `.native.tsx` files alike, and from shared's
 * standalone typecheck.
 */

/**
 * The surface fill — Figma `brand/secondary/130` = `secondary-100 @ 70%`. The
 * missing 30% is meant to be filled by the frost BEHIND it: web's
 * `backdrop-blur`, iOS's `BlurView`.
 */
export const OVERLAY_FILL_CLASS = 'bg-surface-overlay/70';

/**
 * ANDROID fill — the same token at 95%.
 *
 * Android gets no blur here, and the reason is structural rather than a
 * missing prop: `Popover.native` presents through an RN `Modal`, which Android
 * puts in its OWN WINDOW, and expo-blur's Android backend works by re-drawing
 * the view tree of the window it lives in — from inside that modal window
 * there is nothing behind it to sample. (Same finding as
 * `DropdownMenu`, measured on a Pixel 10 emulator.)
 *
 * A 70% fill with no frost behind it is the worst of both: the DS asks for
 * glass and what lands is page content reading straight THROUGH the popover —
 * the "ugly background" on the deck screen's ⊕ create menu. Raising the fill
 * is the honest fallback: it keeps the DS tint, border and radius while making
 * the surface actually opaque. Same trade-off already taken by
 * `DropdownMenu` (0.82 over 0.70 ≈ 0.95) and `CardBodyPanel`. The bottom
 * sheet is the exception: it CAN frost on Android (a gorhom portal lives in
 * the app's own window), so it keeps the 70% fill — see `components/Glass`.
 *
 * Spelled as the DS TOKEN, never a baked hex, so it auto-inverts in dark mode.
 */
export const OVERLAY_FILL_CLASS_ANDROID = 'bg-surface-overlay/95';

/**
 * `border + translucent secondary-100 + glass blur` surface, shared by
 * Simple & Rich. The blur is `web:`-scoped (NativeWind has no
 * backdrop-filter); native mirrors it with an iOS expo-blur BlurView in
 * `FloatingBox.native` (Android swaps in `OVERLAY_FILL_CLASS_ANDROID`).
 */
export const OVERLAY_SURFACE = `border border-secondary-200 ${OVERLAY_FILL_CLASS} web:backdrop-blur-[4px]`;

/**
 * Native twin of the web surface's `shadow-[0px_2px_8px_0px_rgba(0,0,0,0.1)]`
 * (Figma light 2205:13010 / dark 3380:33618 — same value in both) — iOS.
 *
 * Raw RN style, not a NativeWind `shadow-*` class: NativeWind v4 can't
 * reliably compile a CSS box-shadow to RN shadow props, and the stock `shadow`
 * utility is Tailwind's `0 1px 3px` double drop — visibly tighter and harder
 * than the spec. Same split as `TOAST_NATIVE_SHADOW` / `Button.shadows`.
 *
 * iOS derives its layer shadow from the view's COMPOSITED ALPHA, so these four
 * fields are correct on a translucent surface. Android does not — see
 * `OVERLAY_ANDROID_SHADOW`.
 *
 * `FloatingBox.native` picks between the two; both live here (rather than one
 * `Platform.select`) so this file stays import-free plain `.ts`.
 */
export const OVERLAY_NATIVE_SHADOW = {
  shadowColor: '#000000',
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.1,
  shadowRadius: 8,
} as const;

/**
 * The same 0/2/8 @ 10% drop for ANDROID, as `boxShadow`.
 *
 * NOT `elevation` (which this used to carry). An elevation shadow is drawn by
 * the platform from the view's OUTLINE — an opaque, UNROUNDED copy of its
 * bounds, painted outside the view's own alpha. On a see-through surface like
 * this one that copy shows through as a hard-cornered grey rectangle inside
 * the rounded box: the same defect already removed from `CARD_NATIVE_SHADOW`,
 * `FolderTile` and `SegmentedControl`'s active pill.
 *
 * `boxShadow` is drawn as part of the view instead, so it clips to the radius
 * and multiplies by the view's opacity (RN 0.86 / new arch renders it
 * natively — the same support `SegmentedControl.native` and `Toggle.native`
 * already rely on).
 */
export const OVERLAY_ANDROID_SHADOW = {
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
} as const;

/** Radius + padding + layout for the Simple vs Rich forms. */
export function surfaceShape(rich: boolean): string {
  return rich ? 'flex flex-col gap-1 rounded-[20px] p-4' : 'rounded-2xl px-4 py-2.5';
}

/**
 * The numeric twin of `surfaceShape`'s corner radius (`rounded-[20px]` /
 * `rounded-2xl` = 1rem) — keep the two in sync.
 *
 * Web passes it as Radix `arrowPadding`, which is the minimum gap floating-ui
 * keeps between the Tip Arrow's edge and the surface's edge. At exactly the
 * radius the arrow can slide no further than the point where the corner curve
 * ends, so its flat base always lands on a straight edge instead of floating
 * over the curve (which reads as a detached, broken arrow). Boxes too narrow
 * to honour it are handled upstream: floating-ui shrinks the padding and
 * re-centers the arrow rather than overflowing it.
 *
 * `FloatingBox.native` reuses it to clip its blur to the surface's inner edge.
 */
export function surfaceRadius(rich: boolean): number {
  return rich ? 20 : 16;
}

/**
 * Where the Tip Arrow sits along the box's cross axis, in px from the box's
 * leading edge — the native twin of floating-ui's `arrow` middleware.
 *
 * A box that had to be clamped to a screen edge is no longer centred on its
 * trigger, so an arrow pinned at 50% points at empty space. This re-centres it
 * on the trigger and keeps it `pad` (the surface radius) clear of both ends so
 * its base still lands on a straight edge — the same rule web gets from
 * `arrowPadding`, see `surfaceRadius`. A box too narrow to honour that just
 * centres the arrow.
 *
 * Web doesn't need this: Radix/floating-ui already does it.
 */
export function arrowCenter(
  anchorCenter: number,
  boxStart: number,
  boxSize: number,
  pad: number,
): number {
  if (boxSize <= pad * 2) return boxSize / 2;
  return Math.min(Math.max(anchorCenter - boxStart, pad), boxSize - pad);
}

/** Rich title — Figma L3 (Mona Sans Bold 12/16) secondary-900. */
export const OVERLAY_TITLE_CLASS =
  'text-l3 font-bold native:font-mona-bold text-secondary-900';

/**
 * Description body — Figma B3 (Mona Sans Medium 12/20) secondary-700.
 *
 * Web sets this on the surface ROOT, not just on the text node: `content` is a
 * `ReactNode`, and an element (a `<span>` wrapping a hint + gamepad pills, a
 * tiptap tooltip fragment) would otherwise inherit the page's 14/16px body
 * size and render visibly larger than its string-content neighbours. The Rich
 * title re-declares `OVERLAY_TITLE_CLASS`, so it still wins on its own row.
 */
export const OVERLAY_DESC_CLASS =
  'text-b3 font-medium native:font-mona-medium text-secondary-700';

/**
 * Long hints wrap instead of stretching into a single very wide line. Kept
 * alongside the viewport clamp (`calc(100vw-32px)`) so narrow screens still win.
 */
export const OVERLAY_MAX_WIDTH_CLASS = 'max-w-[min(360px,calc(100vw-32px))]';
